/* eslint-disable no-param-reassign,no-shadow,no-unused-vars,null,no-underscore-dangle */
import {
  getInvoiceData,
  fetchProcessedInvoices,
  fetchProcessedByUser,
  getUnprocessedInvoices,
  setInvoiceData,
  editInvoiceStatus,
  downloadFile,
  getInvoiceForReview,
  setExternalRejection,
  axiosInstance,
} from '../../services/api';
import Constants from '../../constants';

const state = {
  invoiceList: [],
  processedInvoices: [],
  activeInvoice: null,
  processedByUser: [],
  unreviewedCount: 0,
  pagination: {
    next: null,
    previous: null,
  },
};

const getters = {
  unProcessedInvoices: (state) => state.invoiceList,
  processedInvoices: (state) => state.processedInvoices,
  activeInvoice: (state) => state.activeInvoice,
  processedByUser: (state) => state.processedByUser,
  unreviewedCount: (state) => state.unreviewedCount,
  pagination: (state) => state.pagination,
};

const mutations = {
  setUnprocessedInvoices(state, payload) {
    state.invoiceList = payload;
  },
  removeInvoiceFromList(state, payload) {
    state.invoiceList = state.invoiceList.filter((invoice) => invoice.url !== payload);
  },
  setActiveInvoice(state, payload) {
    state.activeInvoice = payload;
  },
  updateInvoiceList(state, payload) {
    const item = state.invoiceList.find((x) => x.pk === payload.pk);
    if (item) {
      Object.assign(item, payload);
    } else {
      state.invoiceList.push(payload);
    }
  },
  setProcessedInvoices(state, payload) {
    state.processedInvoices = payload;
  },
  setProcessedByUser(state, payload) {
    state.processedByUser = payload;
  },
  decreaseUnreviewedCount(state, amount) {
    state.unreviewedCount -= amount;
  },
  setUnreviewedCount(state, payload) {
    state.unreviewedCount = payload;
  },
  setPagination(state, payload) {
    state.pagination = payload;
  },
};

const actions = {
  async getNextInvoiceForReview({ commit, dispatch }) {
    try {
      const response = await getInvoiceForReview();
      if (response && response.data) {
        commit('setActiveInvoice', response.data);
        return response.data;
      }
      commit('setActiveInvoice', null);
      return null;
    } catch (e) {
      commit('setActiveInvoice', null);
      throw e;
    }
  },
  async setActiveInvoice({ commit, dispatch }, invoice) {
    try {
      const { data } = await getInvoiceData(invoice.url);
      commit('setActiveInvoice', data);
    } catch (e) {
      commit('setActiveInvoice', null);
      throw e;
    }
  },
  async saveInvoiceToDatabase(
    { dispatch, commit, rootState },
    { selectedInvoice, data, mergedDocumentKeys, documentType, partner }
  ) {
    try {
      let putObject;
      const fileNameArray = selectedInvoice.file_name.split('.');
      fileNameArray.pop();
      const finalFileName = fileNameArray.join('.');
      if (mergedDocumentKeys.length) {
        try {
          const promises = mergedDocumentKeys.map(async (invoice) => {
            const { data: file } = await downloadFile(invoice.file);
            return file;
          });
          const pdfFiles = await Promise.all(promises);
          const mergedPdf = await dispatch('mergeDocuments', pdfFiles);
          const mergedFile = await mergedPdf.save();
          putObject = {
            file: new Blob([mergedFile], { type: 'application/pdf' }),
          };
        } catch (err) {
          throw err;
        }
      }
      const mergedDocumentUrls = mergedDocumentKeys
        .concat()
        .filter((invoice) => invoice !== selectedInvoice);
      putObject = {
        ...putObject,
        details: JSON.stringify(data),
        status: Constants.CONFIRMED,
        confirmed_by: rootState.user.user,
        confirmed_at: new Date().toISOString(),
        document_type: documentType,
      };
      if (partner) {
        putObject.partner = partner;
      }
      const formData = new FormData();
      Object.keys(putObject).forEach((key) => {
        if (key === 'file') formData.append(key, putObject[key], `${finalFileName}.pdf`);
        else formData.append(key, putObject[key]);
      });
      await setInvoiceData(selectedInvoice.url, formData);
      // Remove merged documents from the list and update the status to MERGED
      await dispatch('updateMergedDocuments', mergedDocumentUrls);
      commit('removeInvoiceFromList', selectedInvoice.url);
      commit('decreaseUnreviewedCount', 1);
      return true;
    } catch (err) {
      throw err;
    }
  },
  async updateMergedDocuments({ commit }, payload) {
    const promises = payload.map(async (invoice) => {
      const formData = new FormData();
      formData.append('status', Constants.MERGED);
      formData.append('confirmed_at_date', new Date().toISOString());
      axiosInstance.patch(invoice.url, formData);
    });
    await Promise.all(promises);
    payload.forEach((url) => {
      commit('removeInvoiceFromList', url);
    });
    commit('decreaseUnreviewedCount', payload.length);
  },
  async deleteInvoiceFromDatabase(
    { rootGetters, commit, dispatch, rootState },
    { invoice, rejection, status }
  ) {
    try {
      const formData = new FormData();
      formData.append('status', status);
      formData.append('rejection', rejection);
      formData.append('rejected_by', rootGetters.getUser);
      await axiosInstance.patch(invoice.url, formData);
      commit('removeInvoiceFromList', invoice.url);
      commit('decreaseUnreviewedCount', 1);
      return Promise.resolve('Succesfully deleted invoice');
    } catch (e) {
      return Promise.reject(e);
    }
  },
  async getUnprocessedInvoices({ commit }, url) {
    try {
      const { data } = await getUnprocessedInvoices(url);
      commit('setUnreviewedCount', data.count);
      commit('setUnprocessedInvoices', data.results);
      commit('setPagination', { next: data.next, previous: data.previous });
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getProcessedInvoices({ commit }, queryParams) {
    try {
      const { data } = await fetchProcessedInvoices(queryParams);
      const invoices = data.results.map((invoice) => ({
        ...invoice,
        confirmed_by: invoice.confirmed_by || '',
        confirmed_at: invoice.confirmed_at || '',
        exported_at: invoice.exported_at || '',
        sent_date: invoice.attachment ? invoice.attachment.created_at : invoice.created_at,
        from_mail: invoice.attachment ? invoice.attachment.received_mail.from_mail : 'Unija Scan',
        subject: invoice.attachment ? invoice.attachment.received_mail.subject : '',
        partner_name: (invoice.partner && invoice.partner.name) || '',
        partner_vat_id: (invoice.partner && invoice.partner.vat_id) || '',
        partner_country: (invoice.partner && invoice.partner.country) || '',
        export_path: invoice.export_path || '',
        external_id: invoice.external_id || '',
        document_source: invoice.document_source || '',
      }));
      commit('setProcessedInvoices', invoices);
      return data;
    } catch (err) {
      throw err;
    }
  },
  async returnToProcessing(_, url) {
    try {
      return editInvoiceStatus(url, Constants.WAITING_REVIEW);
    } catch (err) {
      throw err;
    }
  },
  async setExternalRejectionAction(_, invoiceId) {
    try {
      return setExternalRejection(invoiceId);
    } catch (err) {
      throw err;
    }
  },
  async getProcessedByUserAction({ commit }, payload) {
    try {
      const { data } = await fetchProcessedByUser(payload);
      commit('setProcessedByUser', data);
    } catch (err) {
      throw err;
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
